import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { IconChevronLewo, IconOstrzezenie } from "../../components/icons/index.js";
import { PoleOdpowiedzi } from "../../components/trening/PoleOdpowiedzi.js";
import { PotwierdzenieZakonczenia } from "../../components/trening/PotwierdzenieZakonczenia.js";
import { Rozmowa } from "../../components/trening/Rozmowa.js";
import { WidokOceny } from "../../components/trening/WidokOceny.js";
import { Badge } from "../../components/ui/Badge.js";
import { Banner } from "../../components/ui/Banner.js";
import { Button } from "../../components/ui/Button.js";
import { Card } from "../../components/ui/Card.js";
import { ProgressBar } from "../../components/ui/ProgressBar.js";
import { StanBledu, StanLadowania } from "../../components/ui/stany.js";
import { useTrening } from "../../hooks/useTrening.js";
import { useTytulStrony } from "../../hooks/useTytulStrony.js";
import {
  czyLimitTur,
  czyMoznaZakonczyc,
  czyOczekujeNaRozmowce,
  licznikTur,
  odczytajSzkic,
  powodBlokadyZakonczenia,
  usunSzkic,
  zapiszSzkic,
} from "../../lib/trening.js";

/**
 * Ekran jednego treningu (/cwiczenia/:id): rozmowa z symulowanym rozmowca, a po zakonczeniu ocena (takze dla treningu
 * otwartego z historii: wtedy rozmowa jest tylko do odczytu).
 *
 * Stan zmienia sie tylko po akcjach uzytkownika (useTrening): wyslanie odpowiedzi trwa tyle, ile odpowiedz rozmowcy, bo
 * rozmowca odpowiada w tej samej odpowiedzi HTTP. W tym czasie pole jest zablokowane, a tekst zostaje (i w sessionStorage:
 * szkic przezywa odswiezenie strony). Po bledzie tekst nadal jest w polu, a pod nim zdanie z komunikatBleduTreningu.
 */

const ID_POMOCY_KONCA = "trening-koniec-pomoc";

/**
 * Zdanie nad rozmowa: kto gra rozmowce. Stoi zawsze, takze bez opisu rozmowcy, bo na ten ekran mozna wejsc wprost
 * (/cwiczenia/:id z zakladki), z pominieciem zdania na ekranie "Ćwiczenia". Swiadomy wyjatek od reguly, ze ekrany
 * uzytkownika nie mowia o AI (docs/widok-uzytkownika.md).
 */
function opisRozmowcy(scenariusz: string | null | undefined): string {
  return scenariusz ? `Rozmówca (gra go AI): ${scenariusz}` : "Rozmówcę gra AI.";
}

/** Przewiniecie bez zabierania fokusu: `scrollIntoView` nie ruszy fokusu, a przy ograniczonym ruchu skacze bez animacji. */
function przewinDoElementu(el: HTMLElement | null): void {
  if (!el || typeof el.scrollIntoView !== "function") return;
  const ograniczonyRuch =
    typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  el.scrollIntoView({ block: "nearest", behavior: ograniczonyRuch ? "auto" : "smooth" });
}

export function CwiczenieSesjaPage(): React.JSX.Element {
  const { id = "" } = useParams();
  // `key`: przejscie z jednego treningu na inny ("Ćwicz jeszcze raz") zaczyna ekran od zera, bez szkicu i bledow poprzedniego.
  return <SesjaTreningu key={id} id={id} />;
}

function SesjaTreningu({ id }: { id: string }): React.JSX.Element {
  const t = useTrening(id);
  const trening = t.trening;
  useTytulStrony(trening ? `Ćwiczenie: ${trening.parametr} - ConvoLens` : null);

  const [szkic, setSzkic] = useState(() => odczytajSzkic(id));
  const [dialogZakonczenia, setDialogZakonczenia] = useState(false);
  const [fokusNaOcene, setFokusNaOcene] = useState(false);
  const poleRef = useRef<HTMLTextAreaElement>(null);
  const koniecRef = useRef<HTMLDivElement>(null);
  const ocenaRef = useRef<HTMLHeadingElement>(null);
  const poprzedniaLiczba = useRef<number | null>(null);

  const liczbaWiadomosci = trening?.wiadomosci.length ?? null;

  // Nowe wiadomosci (nie pierwsze wczytanie): przewijamy do konca karty rozmowy, tak by widac bylo i odpowiedz, i pole.
  useEffect(() => {
    if (liczbaWiadomosci === null) return;
    const poprzednia = poprzedniaLiczba.current;
    poprzedniaLiczba.current = liczbaWiadomosci;
    if (poprzednia !== null && liczbaWiadomosci > poprzednia) przewinDoElementu(koniecRef.current);
  }, [liczbaWiadomosci]);

  // Po zakonczeniu przycisk, ktory mial fokus, znika razem z karta: fokus idzie na naglowek oceny (nie na <body>).
  // Dotyczy KAZDEGO przejscia z aktywnego na zakonczony, takze gdy trening zakonczono w innej karcie, a ekran dowiedzial sie
  // o tym dopiero po kliknieciu "Wyślij" albo "Zakończ" (409 trening_zakonczony i ponowne pobranie).
  const status = trening?.status;
  const poprzedniStatus = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (poprzedniStatus.current === "ACTIVE" && status === "FINISHED") setFokusNaOcene(true);
    poprzedniStatus.current = status;
  }, [status]);
  useEffect(() => {
    if (!fokusNaOcene || !ocenaRef.current) return;
    ocenaRef.current.focus();
    setFokusNaOcene(false);
  }, [fokusNaOcene, status]);

  const naglowekStrony = (
    <>
      <nav className="trening-powrot" aria-label="Powrót">
        <Link className="trening-powrot__link" to="/cwiczenia">
          <IconChevronLewo size={16} />
          Wróć do ćwiczeń
        </Link>
        {trening?.zrodloAnalizyId && (
          <Link className="trening-powrot__link" to={`/analizy/${encodeURIComponent(trening.zrodloAnalizyId)}`}>
            <IconChevronLewo size={16} />
            Wróć do wyniku rozmowy
          </Link>
        )}
      </nav>
      <h1 className="page-title">{trening ? `Ćwiczenie: ${trening.parametr}` : "Ćwiczenie"}</h1>
    </>
  );

  if (!trening) {
    const blad = t.bladLadowania;
    return (
      <>
        {naglowekStrony}
        <Card>
          {blad === null ? (
            <StanLadowania tekst="Wczytuję ćwiczenie..." />
          ) : blad.status === 404 || blad.kod === "nie_ma_takiego_treningu" ? (
            <StanBledu
              tytul="Nie ma takiego ćwiczenia"
              opis="Sprawdź adres albo wróć do listy swoich ćwiczeń."
              akcja={
                <Link className="btn btn--secondary" to="/cwiczenia">
                  Wróć do ćwiczeń
                </Link>
              }
            />
          ) : (
            <StanBledu
              tytul="Nie udało się wczytać ćwiczenia"
              opis={blad.zdanie}
              akcja={
                <Button wariant="secondary" onClick={t.odswiez}>
                  Spróbuj ponownie
                </Button>
              }
            />
          )}
        </Card>
      </>
    );
  }

  const zakonczony = trening.status === "FINISHED";

  if (zakonczony) {
    return (
      <>
        {naglowekStrony}
        <Banner wariant="info" tekst={trening.cwiczenieTekst} />
        <WidokOceny trening={trening} naglowekRef={ocenaRef} />
        <Card tytul="Rozmowa" akcje={<Badge>Poziom: {trening.poziomNazwa}</Badge>}>
          <p className="trening-scenariusz">{opisRozmowcy(trening.scenariusz)}</p>
          <Rozmowa wiadomosci={trening.wiadomosci} aktywna={false} />
        </Card>
      </>
    );
  }

  const limit = czyLimitTur(trening);
  const oczekuje = czyOczekujeNaRozmowce(trening);
  const rozmowcaPisze = t.wysylanie || oczekuje;
  const zablokowane = t.wysylanie || t.konczenie || oczekuje || limit;
  const moznaZakonczyc = czyMoznaZakonczyc(trening);
  // Zakonczyc nie wolno, dopoki jest za malo odpowiedzi albo rozmowca jeszcze odpowiada: powod stoi tekstem pod przyciskiem (S-10).
  const powodBlokady =
    powodBlokadyZakonczenia(trening) ?? (t.wysylanie || oczekuje ? "Poczekaj na odpowiedź rozmówcy." : null);
  const bladZakonczenia = t.bladAkcji?.akcja === "zakoncz" ? t.bladAkcji : null;

  const podpowiedz = limit
    ? `To była ostatnia możliwa odpowiedź (${licznikTur(trening)}). Zakończ ćwiczenie, żeby zobaczyć ocenę.`
    : t.wysylanie
      ? "Poczekaj na odpowiedź rozmówcy."
      : oczekuje
        ? "Rozmówca jeszcze odpowiada. Odśwież rozmowę, żeby sprawdzić, czy już odpowiedział."
        : "Ctrl+Enter wysyła odpowiedź.";

  function zmienSzkic(tekst: string): void {
    setSzkic(tekst);
    zapiszSzkic(id, tekst);
  }

  async function wyslij(): Promise<void> {
    const tekst = szkic.trim();
    if (tekst === "" || zablokowane) return;
    const ok = await t.wyslij(tekst);
    if (!ok) return; // Blad: tekst zostaje w polu i w szkicu, zdanie pokazuje pole.
    setSzkic("");
    usunSzkic(id);
    // Pole nie bylo `disabled`, wiec zwykle ma fokus; po kliknieciu "Wyślij" mial go przycisk, wiec oddajemy go polu.
    poleRef.current?.focus({ preventScroll: true });
  }

  async function zakonczIPokazOcene(): Promise<void> {
    const ok = await t.zakoncz();
    // Okno trzymalo sie otwarte z zajetym przyciskiem na czas oceny (DG-5); po wyniku znika, a ewentualny blad stoi pod przyciskiem w karcie.
    setDialogZakonczenia(false);
    if (!ok) return;
    usunSzkic(id);
    setFokusNaOcene(true);
  }

  return (
    <>
      {naglowekStrony}
      <Banner wariant="info" tekst={trening.cwiczenieTekst} />

      <Card
        tytul="Rozmowa"
        akcje={
          <>
            <Badge>Poziom: {trening.poziomNazwa}</Badge>
            <Badge>Odpowiedzi: {licznikTur(trening)}</Badge>
          </>
        }
      >
        <p className="trening-scenariusz">{opisRozmowcy(trening.scenariusz)}</p>
        {trening.wiadomosci.length === 0 && !rozmowcaPisze && (
          <p className="trening-pusta">Napisz pierwszą wiadomość, żeby zacząć rozmowę.</p>
        )}
        <Rozmowa wiadomosci={trening.wiadomosci} rozmowcaPisze={rozmowcaPisze} />
        <PoleOdpowiedzi
          wartosc={szkic}
          onZmiana={zmienSzkic}
          onWyslij={() => void wyslij()}
          onOdswiez={oczekuje && !t.wysylanie ? t.odswiez : undefined}
          zablokowane={zablokowane}
          wysylanie={t.wysylanie}
          podpowiedz={podpowiedz}
          blad={t.bladAkcji}
          poleRef={poleRef}
        />
        <div ref={koniecRef} aria-hidden="true" />
      </Card>

      <Card>
        <div className="trening-koniec">
          <div className="trening-koniec__tekst">
            <p className="trening-koniec__tytul">Po rozmowie dostaniesz ocenę.</p>
            <p className="trening-pomoc" id={ID_POMOCY_KONCA}>
              {powodBlokady ?? "Zobaczysz, w których odpowiedziach udało Ci się wykonać ćwiczenie i co poprawić."}
            </p>
          </div>
          {/* B-2: przy limicie odpowiedzi zakonczenie jest jedyna sensowna akcja, wiec dostaje wariant primary. */}
          <Button
            wariant={limit ? "primary" : "secondary"}
            wylaczony={!moznaZakonczyc || t.wysylanie || oczekuje}
            zajety={t.konczenie}
            aria-describedby={ID_POMOCY_KONCA}
            onClick={() => setDialogZakonczenia(true)}
          >
            Zakończ i pokaż ocenę
          </Button>
        </div>
        {/* PR-1, PR-3: ocena trwa dluzej niz dwie sekundy - pasek w tej samej karcie, nie nakladka. */}
        {t.konczenie && <ProgressBar etykieta="Oceniamy Twoje odpowiedzi..." />}
        {bladZakonczenia && (
          <p className="field-error" role="alert">
            <IconOstrzezenie size={14} />
            {bladZakonczenia.zdanie}
          </p>
        )}
      </Card>

      {/* Zakonczenie niczego nie niszczy, wiec zwykly primary, a nie czerwony przycisk ConfirmDialog (DG-1). */}
      <PotwierdzenieZakonczenia
        otwarty={dialogZakonczenia}
        zajety={t.konczenie}
        onAnuluj={() => setDialogZakonczenia(false)}
        onPotwierdz={() => void zakonczIPokazOcene()}
      />
    </>
  );
}
